import { useCallback, useRef, useState } from "react";
import { Modal } from "risa-oasis-ui_v2";
import {
  bigquerySearchLike,
  BIGQUERY_SEARCH_LIKE_DATASET_NAME,
  BIGQUERY_SEARCH_LIKE_TABLE_NAME,
} from "../../../api/searchLike";
import { FORM_MATRIX_FORM_NAME_OPTIONS } from "../../../enums/formMatrixFormNames";
import { ModalId } from "../../../enums/modalId";
import { FormMatrixConfigItem } from "../../../pages/configurations/pages/validationFunnel/pages/formMatrix/hooks/useFormMatrixData";
import AsyncSearchableSelect from "./components/asyncSearchableSelect";
import SwitchContainers from "./components/switchContainers";

interface AddFormMatrixProps {
  onCancel: () => void;
  onSave: (data: FormMatrixConfigItem) => void | Promise<void>;
  isSaving?: boolean;
}

const INITIAL_SWITCH_STATE = {
  icd: false,
  dosage: false,
  dosageInstr: false,
  ev: false,
  daw: false,
  reviewNeeded: false,
};

const SEARCH_COLUMN = "form_name";
const SEARCH_LIMIT = 10;
const CREATED_AT_CUTOFF_MONTHS = 3;

const AddFormMatrix = ({
  onCancel,
  onSave,
  isSaving = false,
}: AddFormMatrixProps) => {
  const [formName, setFormName] = useState("");
  const [asyncFormName, setAsyncFormName] = useState("");
  const [switchValues, setSwitchValues] =
    useState<Record<string, boolean>>(INITIAL_SWITCH_STATE);
  const [resetKey, setResetKey] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  const handleSwitchChange = (data: {
    name: string;
    required: boolean;
    value: boolean;
  }) => {
    setSwitchValues((prev) => ({ ...prev, [data.name]: data.value }));
  };

  const handleAsyncSearch = useCallback(async (query: string) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const results = await bigquerySearchLike(
        {
          distinct: true,
          select_column: SEARCH_COLUMN,
          filter_column: SEARCH_COLUMN,
          query,
          limit: SEARCH_LIMIT,
          created_at_cutoff_months: CREATED_AT_CUTOFF_MONTHS,
          dataset_name: BIGQUERY_SEARCH_LIKE_DATASET_NAME,
          table_name: BIGQUERY_SEARCH_LIKE_TABLE_NAME,
        },
        controller.signal,
      );
      return results.map((text) => ({ value: text, label: text }));
    } catch {
      return [];
    }
  }, []);

  const resetForm = () => {
    setFormName("");
    setAsyncFormName("");
    setSwitchValues(INITIAL_SWITCH_STATE);
    setResetKey((k) => k + 1);
  };

  const getFormData = (): FormMatrixConfigItem => ({
    form_name: formName.trim(),
    icd: switchValues.icd ?? false,
    dosage: switchValues.dosage ?? false,
    dosage_instr: switchValues.dosageInstr ?? false,
    ev: switchValues.ev ?? false,
    daw: switchValues.daw ?? false,
    review_needed: switchValues.reviewNeeded ?? false,
  });

  const handleSave = async () => {
    const payload = getFormData();
    await onSave(payload);
    resetForm();
  };

  const canSave = formName.trim().length > 0;
  const saveDisabled = !canSave || isSaving;

  return (
    <Modal
      dialogId={ModalId.ADD_FORM_MATRIX_DIALOG}
      title="Add Form Matrix"
      onCancel={() => {
        resetForm();
        onCancel();
      }}
      onClose={() => {
        resetForm();
        onCancel();
      }}
      onSave={handleSave}
      saveButtonText={isSaving ? "Saving..." : "Save"}
      cancelText="Close"
      hideFooter={false}
      disableSave={saveDisabled}
    >
      <div key={resetKey}>
        {/* <SearchableSelect
          id="formName"
          label="Form Name"
          placeholder="Select Form Name"
          options={FORM_MATRIX_FORM_NAME_OPTIONS}
          onOptionChange={(option) => setFormName(option.value ?? "")}
          required
        /> */}
        <AsyncSearchableSelect
          id="asyncFormName"
          label="Search Form Name"
          placeholder="Type to search..."
          initialOptions={FORM_MATRIX_FORM_NAME_OPTIONS}
          onSearch={handleAsyncSearch}
          onOptionChange={(option) => setAsyncFormName(option.value ?? "")}
          minSearchLength={2}
          debounceMs={300}
          noResultsMessage="No results found"
        />
        <SwitchContainers onChangeEmit={handleSwitchChange} />
      </div>
    </Modal>
  );
};

export default AddFormMatrix;
